import React, { useState } from 'react';
import { RotateCcw, X, Info, Loader2, ArrowRight } from 'lucide-react';

interface ReactivateSubscriptionModalProps {
  open: boolean;
  onClose: () => void;
  workspaceName?: string;
  hasChargebeeSubscription: boolean;
  currentPeriodEnd: string | null;
  onConfirm: () => Promise<void>;
}

const ReactivateSubscriptionModal: React.FC<ReactivateSubscriptionModalProps> = ({
  open,
  onClose,
  workspaceName,
  hasChargebeeSubscription,
  currentPeriodEnd,
  onConfirm,
}) => {
  const [loading, setLoading] = useState(false);
  const [confirmHovered, setConfirmHovered] = useState(false);
  const [cancelHovered, setCancelHovered] = useState(false);
  const [closeHovered, setCloseHovered] = useState(false);

  const endDate = currentPeriodEnd
    ? new Date(currentPeriodEnd).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      // Error handling done in parent
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '20px',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'var(--v2-surface)',
          borderRadius: 'var(--radius-lg)',
          boxShadow: 'var(--shadow-lg)',
          width: '100%',
          maxWidth: '480px',
          maxHeight: '90vh',
          overflowY: 'auto',
          fontFamily: 'var(--font-sans)',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '20px 24px',
            borderBottom: '1px solid var(--v2-border)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div
              style={{
                width: '36px',
                height: '36px',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--v2-primary-light)',
                color: 'var(--v2-primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <RotateCcw size={20} strokeWidth={1.5} />
            </div>
            <h2 style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--v2-text)', margin: 0 }}>
              Reactivate Subscription
            </h2>
          </div>
          <button
            onClick={onClose}
            onMouseEnter={() => setCloseHovered(true)}
            onMouseLeave={() => setCloseHovered(false)}
            style={{
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: closeHovered ? 'var(--v2-surface-hover)' : 'transparent',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
              color: 'var(--v2-text-muted)',
              transition: 'background-color 0.15s ease',
            }}
          >
            <X size={18} strokeWidth={1.5} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          {/* Info Alert */}
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '12px',
              padding: '14px 16px',
              backgroundColor: 'var(--v2-info-bg)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid #93c5fd',
              marginBottom: '20px',
            }}
          >
            <Info size={16} strokeWidth={1.5} color="var(--v2-info-dark)" style={{ flexShrink: 0, marginTop: '1px' }} />
            <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--v2-text-secondary)' }}>
              {hasChargebeeSubscription ? (
                <>
                  Reactivate your subscription for <strong style={{ color: 'var(--v2-text)' }}>{workspaceName}</strong>?
                  {endDate && <> Your billing will resume and access will continue uninterrupted.</>}
                </>
              ) : (
                <>
                  To reactivate <strong style={{ color: 'var(--v2-text)' }}>{workspaceName}</strong>, you'll need to
                  select a plan and complete checkout.
                </>
              )}
            </p>
          </div>

          {/* What happens next */}
          <div
            style={{
              backgroundColor: 'var(--v2-surface-alt)',
              borderRadius: 'var(--radius-md)',
              padding: '16px',
            }}
          >
            <h4
              style={{
                fontSize: '0.8125rem',
                fontWeight: 600,
                color: 'var(--v2-text)',
                margin: '0 0 12px 0',
              }}
            >
              What happens next:
            </h4>
            <ul
              style={{
                margin: 0,
                paddingLeft: '20px',
                fontSize: '0.8125rem',
                color: 'var(--v2-text-tertiary)',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
              }}
            >
              {hasChargebeeSubscription ? (
                <>
                  <li>Your subscription will be reactivated immediately</li>
                  <li>Billing will resume on your next billing date</li>
                  <li>All workspace data and settings are preserved</li>
                </>
              ) : (
                <>
                  <li>You'll be redirected to select a plan</li>
                  <li>Complete checkout to start your subscription</li>
                  <li>All workspace data and settings are preserved</li>
                </>
              )}
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: '12px',
            padding: '16px 24px',
            borderTop: '1px solid var(--v2-border)',
            backgroundColor: 'var(--v2-surface-alt)',
          }}
        >
          <button
            onClick={onClose}
            disabled={loading}
            onMouseEnter={() => setCancelHovered(true)}
            onMouseLeave={() => setCancelHovered(false)}
            style={{
              padding: '10px 20px',
              backgroundColor: cancelHovered && !loading ? 'var(--v2-surface-hover)' : 'var(--v2-surface)',
              color: loading ? 'var(--v2-text-muted)' : 'var(--v2-text)',
              border: '1px solid var(--v2-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.15s ease',
              opacity: loading ? 0.6 : 1,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading}
            onMouseEnter={() => setConfirmHovered(true)}
            onMouseLeave={() => setConfirmHovered(false)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '10px 20px',
              backgroundColor: loading ? 'var(--v2-border)' : confirmHovered ? 'var(--v2-primary-hover)' : 'var(--v2-primary)',
              color: loading ? 'var(--v2-text-muted)' : '#ffffff',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              fontSize: '0.875rem',
              fontWeight: 500,
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'background-color 0.15s ease',
            }}
          >
            {loading ? (
              <>
                <Loader2 size={16} strokeWidth={2} style={{ animation: 'spin 1s linear infinite' }} />
                Processing...
              </>
            ) : hasChargebeeSubscription ? (
              <>
                <RotateCcw size={16} strokeWidth={2} />
                Reactivate Now
              </>
            ) : (
              <>
                Continue to Checkout
                <ArrowRight size={16} strokeWidth={2} />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReactivateSubscriptionModal;
